/**
 * CSTR balans tenglamalari (turg'un va dinamik uchun umumiy qismlar).
 *
 * TURG'UN MODDIY BALANS (A bo'yicha):
 *   F·(C_A0 − C_A) = V·(−r_A)   →   X = Da·(1 − X)^n,  Da = k(T)·τ·C_A0^(n−1)
 *   n = 1: X = Da/(1 + Da) (analitik); umumiy n: g(X) = X − Da·(1−X)^n monoton → yagona ildiz.
 *
 * TURG'UN ISSIQLIK BALANSI (K birligida, F·ρ·cp ga bo'lingan):
 *   H(T) = (Tin − T) + ΔT_ad·X(T) − κ·(T − Tj) = 0,   κ = U·A/(F·ρ·cp)
 *   "Issiqlik hosil bo'lish" G(T) = ΔT_ad·X(T) S-shaklida, "chiqarish" R(T) chiziqli →
 *   ekzotermik holatda 1 yoki 3 kesishish (Levenspiel 9-bob, Fogler 12-bob).
 *
 * DINAMIK TIZIM:
 *   dC_A/dt = (F/V)(C_A0 − C_A) − (−r_A)
 *   dC_B/dt = (F/V)(C_B0 − C_B) + (b/a)(−r_A)
 *   ρ·cp·V·dT/dt = F·ρ·cp·(Tin − T) + (−ΔH_r)·V·(−r_A) − U·A·(T − Tj)
 *
 * BARQARORLIK: (C_A, T) bo'yicha 2×2 Yakobian; tr < 0 va det > 0 → barqaror.
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR (ASSUMPTION)
 *   1. Ideal aralashtirish: reaktor ichi va chiqish bir xil (C_A, T).
 *   2. Ko'ylak harorati Tj o'zgarmas (ko'ylak dinamikasi yo'q), U·A o'zgarmas.
 *   3. Isothermal rejimda T = Tin: kerakli issiqlik cheksiz tez chiqariladi.
 *   4. Suyuqlik sathi o'zgarmas (kirish sarfi = chiqish sarfi), V doimiy.
 *   5. Kinetika/xossalar farazlari — reaction/kinetics.ts.
 *
 * MANBA: Levenspiel, CRE 3-nashr, 5-bob (CSTR) va 9-bob (issiqlik effektlari);
 *        Fogler, ECRE, 12-bob (ko'p turg'un holat); Kasatkin (reaktorlar bobi).
 */
import { bisection, newtonScalar } from '../../solvers';
import { R_GAS } from '../../properties';
import {
  arrhenius,
  damkohler,
  rateDerivative,
  reactionRate,
  validateReaction,
  type ReactionMixture,
} from '../reaction';
import type { CstrInputs, CstrParams } from './types';

/** Konstruktiv va rejim parametrlari tekshiruvi */
export function validateCstrParams(p: CstrParams): void {
  if (!(p.volume > 0)) throw new Error('cstr: volume must be > 0');
  validateReaction('cstr', p.reaction);
  if (p.mode === 'jacketed') {
    if (!(p.U !== undefined && p.U >= 0)) throw new Error('cstr: U is required (≥ 0) for jacketed');
    if (!(p.area !== undefined && p.area >= 0))
      throw new Error('cstr: area is required (≥ 0) for jacketed');
  }
  if (p.Tmax !== undefined && !(p.Tmax > 0)) throw new Error('cstr: Tmax must be > 0');
}

export function validateCstrInputs(p: CstrParams, i: CstrInputs): void {
  if (!(i.flowRate >= 0)) throw new Error('cstr: flowRate must be ≥ 0');
  if (!(i.CA0 >= 0)) throw new Error('cstr: CA0 must be ≥ 0');
  if (i.CB0 !== undefined && !(i.CB0 >= 0)) throw new Error('cstr: CB0 must be ≥ 0');
  if (!(i.Tin > 0)) throw new Error('cstr: Tin must be > 0 K');
  if (p.mode === 'jacketed' && !(i.Tjacket !== undefined && i.Tjacket > 0))
    throw new Error('cstr: Tjacket is required (> 0 K) for jacketed');
}

/** U·A, W/K (jacketed bo'lmasa 0) */
export function heatTransferUA(p: CstrParams): number {
  return p.mode === 'jacketed' ? (p.U as number) * (p.area as number) : 0;
}

/**
 * Turg'un moddiy balansdan X(T): X = Da·(1−X)^n.
 * n = 1 analitik; aks holda Newton (chegaralangan) + bisection zaxira.
 */
export function conversionAt(
  T: number,
  tau: number,
  CA0: number,
  k0: number,
  Ea: number,
  order: number,
): number {
  const k = arrhenius(k0, Ea, T);
  const Da = damkohler(k, tau, CA0, order);
  if (!(Da > 0)) return 0;
  if (!Number.isFinite(Da)) return 1;
  if (order === 1) return Da / (1 + Da);
  const g = (X: number) => X - Da * Math.pow(Math.max(0, 1 - X), order);
  const x0 = Da / (1 + Da);
  const nr = newtonScalar(g, x0, { bounds: [0, 1], ftol: 1e-14, xtol: 1e-14 });
  if (nr.converged) return nr.value;
  const bs = bisection(g, 0, 1, { xtol: 1e-15, ftol: 1e-14 });
  return bs.converged ? bs.value : x0;
}

/**
 * Issiqlik balansi qoldig'i H(T), K. κ = UA/(F·ρ·cp).
 * H(T) = (Tin − T) + ΔT_ad·X(T) − κ·(T − Tj)
 */
export function heatResidual(
  T: number,
  X: number,
  Tin: number,
  dTad: number,
  kappa: number,
  Tj: number,
): number {
  return Tin - T + dTad * X - kappa * (T - Tj);
}

/**
 * Chiziqlashtirilgan (C_A, T) tizimining barqarorligi.
 *   J11 = −F/V − n·k·C_A^(n−1)                J12 = −k·C_A^n·Ea/(R·T²)
 *   J21 = (−ΔH)·n·k·C_A^(n−1)/(ρcp)           J22 = −F/V + (−ΔH)·k·C_A^n·Ea/(R·T²·ρcp) − UA/(ρcp·V)
 * Isothermal rejimda T doimiy → faqat J11 < 0 (har doim barqaror).
 */
export function isStable(
  p: CstrParams,
  mix: ReactionMixture,
  F: number,
  CA: number,
  T: number,
): boolean {
  const V = p.volume;
  const k = arrhenius(p.reaction.k0, p.reaction.Ea, T);
  const CAeff = Math.max(CA, 1e-300);
  const r = reactionRate(k, CAeff, mix.order);
  const drdC = rateDerivative(k, CAeff, mix.order);
  const drdT = (r * p.reaction.Ea) / (R_GAS * T * T);
  const J11 = -F / V - drdC;
  if (p.mode === 'isothermal') return J11 < 0;
  const UA = heatTransferUA(p);
  const J12 = -drdT;
  const J21 = (mix.heatRelease * drdC) / mix.rhoCp;
  const J22 = -F / V + (mix.heatRelease * drdT) / mix.rhoCp - UA / (mix.rhoCp * V);
  const tr = J11 + J22;
  const det = J11 * J22 - J12 * J21;
  return tr < 0 && det > 0;
}
